import { useEffect } from 'react';
import * as R from 'remeda';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Redirect, RouteComponentProps } from 'react-router';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { Paper, Stack, Typography } from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { notify } from '../../toaster';
import { ColumnTile } from '../columns/ColumnTile';
import { DatasetDetailLayout } from '../dataset-detail/DatasetDetailLayout';
import { useTable } from '../hooks/useTable';
import { useUpdateTable } from '../hooks/useUpdateTable';
import { datasetsPath } from '../routes';
import { pageSpacingStyles } from '../styles';
import { Dataset, getColumnsFromMetadata } from '../types';
import { formSchema, FormSchema } from './types';

const FORM_ID = 'dataset-settings';

export const DatasetSettings = ({ table }: { table: Dataset }) => {
  const intl = useIntl();

  const { mutate: updateTable, isLoading: isUpdating } = useUpdateTable();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitSuccessful },
    reset,
  } = useForm<FormSchema>({
    defaultValues: {
      name: table.name,
      metadata: {
        ...table.metadata,
        columnNames: R.mapValues(
          table.schema?.properties ?? {},
          (_, key) => table.metadata.columnNames[key] ?? key
        ),
      },
    },
    resolver: zodResolver(formSchema(intl)),
    mode: 'onSubmit',
  });

  const schemaColumns = getColumnsFromMetadata(table.metadata, table.schema);

  const submitDataset = (form: FormSchema) => {
    if (!isValid) {
      return;
    }

    updateTable(
      {
        tableName: table.name,
        table: form,
      },
      {
        onSuccess: () => {
          notify.default({
            title: intl.formatMessage({
              id: 'features.datasets.notification.updateSucceeded',
            }),
          });
        },
        onError: () => {
          notify.error({
            title: intl.formatMessage({
              id: 'features.datasets.notification.updateFailed',
            }),
          });
        },
      }
    );
  };

  useEffect(() => {
    // Reset form state when form is submitted (so that you don't see leaving dialog)
    // TODO: isSubmitSuccessful does not actually check success now at all.
    reset(undefined, { keepValues: true });
  }, [isSubmitSuccessful, reset]);

  return (
    <DatasetDetailLayout
      table={table}
      tab="settings"
      hasPendingChanges={isDirty}
      save={{
        label: intl.formatMessage({
          id: 'features.datasets.settings.header.button.save',
        }),
        disabled: !isDirty || isUpdating,
        loading: isUpdating,
        form: FORM_ID,
      }}
    >
      <LeavingDialog when={isDirty} />

      <Stack
        sx={pageSpacingStyles}
        gap={2}
        component="form"
        id={FORM_ID}
        onSubmit={handleSubmit(submitDataset)}
      >
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.settings.form.idSection.title',
          })}
          description={
            <Stack spacing={1}>
              <Typography variant="inherit">
                {intl.formatMessage({
                  id: 'features.datasets.settings.form.idSection.description',
                })}
              </Typography>
              {/* TODO: Link */}
            </Stack>
          }
        >
          <Stack gap={2}>
            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.name' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.name.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
            />

            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.description' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.description.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              multiline
              maxRows={3}
            />

            {table.name && (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {table.name}
                </Typography>
                <CopyToClipboardButton content={table.name} />
              </Stack>
            )}
          </Stack>
        </ContentGroup>
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.settings.form.columnsSection.title',
          })}
          description={intl.formatMessage({
            id: 'features.datasets.settings.form.columnsSection.description',
          })}
          isFullWidthLayout
        >
          <Stack spacing={0.5}>
            {schemaColumns.length > 0 ? (
              schemaColumns.map(column => (
                <ColumnTile
                  key={column.datasetAttributeName}
                  control={control}
                  labelField={`metadata.columnNames.${column.datasetAttributeName}`}
                  attributeName={column.datasetAttributeName}
                  type={column.type}
                  bsonType={column.bsonType}
                  data-cy="dataset-column-definition"
                />
              ))
            ) : (
              <Stack component={Paper} textAlign="center" p={2} elevation={2}>
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.datasets.settings.form.columnsSection.noColumns.title',
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        </ContentGroup>
      </Stack>
    </DatasetDetailLayout>
  );
};

export const DatasetSettingsWrapper = ({
  match: {
    params: { id },
  },
}: RouteComponentProps<{ id: string }>) => {
  const { data: table, isSuccess, isFetching } = useTable(id);
  // A deleted dataset leaves the list as soon as its delete is accepted. A
  // cached list may predate a new dataset, so wait for the refetch.
  if (isSuccess && !isFetching && !table)
    return <Redirect to={datasetsPath()} />;
  return table ? <DatasetSettings table={table} /> : null;
};
